import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getHeaderConfig, getCategories, searchProducts } from '../firebase/services';
import { useCart } from '../Context/CartContext';
import { useLanguage } from '../Context/LanguageContext';
import { useWishlist } from '../Context/WishlistContext';
import '../header-fixes.css';
import '../header-fixes-mobile.css';

export default function Header() {
  const [navActive, setNavActive] = useState(false);
  const [expandedMobileParentId, setExpandedMobileParentId] = useState(null);
  const [logoUrl, setLogoUrl] = useState('/assets/images/logo.png');
  const [categories, setCategories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false); // State for mobile search
  const searchRef = useRef(null);
  const searchDebounceRef = useRef(null);
  const searchReqIdRef = useRef(0);
  const navigate = useNavigate();
  const { getCartCount, toggleCart } = useCart();
  const { getWishlistCount } = useWishlist();
  const { language, toggleLanguage } = useLanguage();

  const toggleNav = () => {
    setNavActive(prev => {
      const next = !prev;
      if (!next) setExpandedMobileParentId(null);
      return next;
    });
  };

  const closeNav = () => {
    setNavActive(false);
    setExpandedMobileParentId(null);
  };

  useEffect(() => {
    const fetchData = async () => {
      const [config, cats] = await Promise.all([
        getHeaderConfig(), 
        getCategories()
      ]);
      if (config && config.logoUrl) {
        setLogoUrl(config.logoUrl);
      }
      setCategories(cats || []);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSearchResults(false);
        setShowCategoryDropdown(false);
        // Close mobile search when clicking outside
        setShowMobileSearch(false);
      }
      
      // Close mobile nav when clicking outside
      const navbar = document.querySelector('.navbar');
      const navToggle = document.querySelector('.nav-toggle-btn');
      if (navbar && navToggle && navbar.classList.contains('active') && 
          !navbar.contains(event.target) && !navToggle.contains(event.target)) {
        setNavActive(false);
        setExpandedMobileParentId(null);
        navbar.classList.remove('active');
      }
    };

    // Handle overlay click - separate from navbar click
    const handleOverlayClick = (event) => {
      // Check if clicking on the overlay (pseudo-element)
      const navbar = document.querySelector('.navbar');
      if (navbar && navbar.classList.contains('active') && 
          event.target === navbar && event.offsetX >= 280) {
        setNavActive(false);
        setExpandedMobileParentId(null);
        navbar.classList.remove('active');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    
    // Add overlay click listener to navbar
    const navbar = document.querySelector('.navbar');
    if (navbar) {
      navbar.addEventListener('click', handleOverlayClick);
    }

    // Add click handler for mobile search icon
    const handleMobileSearchClick = () => {
      setShowMobileSearch(!showMobileSearch);
    };

    // Find and add click listener to the pseudo-element
    const addMobileSearchListener = () => {
      const headerActions = document.querySelector('.header-actions');
      if (headerActions) {
        headerActions.addEventListener('click', (e) => {
          // Check if click is on the pseudo-element area
          const rect = headerActions.getBoundingClientRect();
          const pseudoElementWidth = 40;
          const pseudoElementHeight = 40;
          
          if (e.clientX >= rect.left && e.clientX <= rect.left + pseudoElementWidth &&
              e.clientY >= rect.top && e.clientY <= rect.top + pseudoElementHeight) {
            handleMobileSearchClick();
          }
        });
      }
    };

    // Wait for DOM to be ready
    setTimeout(addMobileSearchListener, 100);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      if (navbar) {
        navbar.removeEventListener('click', handleOverlayClick);
      }
    };
  }, [showMobileSearch]);

  const getProductSlug = (product) => {
    let name = '';
    if (product?.nameEn) {
      name = product.nameEn;
    } else if (typeof product?.name === 'string') {
      name = product.name;
    } else if (typeof product?.name === 'object' && product.name !== null) {
      name = product.name.en || product.name.ar || '';
    } else if (product?.nameAr) {
      name = product.nameAr;
    }

    const firstPart = (name || '').split(' / ')[0];
    return firstPart.toLowerCase().replace(/\s+/g, '-');
  };

  const getProductCategorySlug = (product) => {
    const rawPrimary = (product?.categories && Array.isArray(product.categories) && product.categories.length > 0)
      ? product.categories[0]
      : product?.category;

    const resolvedCategory = categories.find(c => c.id === rawPrimary) || null;
    const label = resolvedCategory
      ? (getName(resolvedCategory) || resolvedCategory.name)
      : (typeof rawPrimary === 'string' ? rawPrimary : 'uncategorized');

    const firstPart = (label || 'uncategorized').split(' / ')[0];
    return firstPart.toLowerCase().replace(/\s+/g, '-');
  };

  const handleSearchResultSelect = (product) => {
    const categorySlug = getProductCategorySlug(product);
    const productSlug = getProductSlug(product);
    const url = `/products/${categorySlug}/${productSlug}`;

    setShowSearchResults(false);
    setShowCategoryDropdown(false);
    setShowMobileSearch(false);
    navigate(url);
  };

  const getName = (item) => {
    if (language === 'ar') return item.nameAr || item.name;
    if (item.nameEn) return item.nameEn;
    if (item.name && item.name.includes(' / ')) return item.name.split(' / ')[0];
    return item.name;
  };

  const toSlug = (value, fallback = '') => {
    const raw = (value ?? '').toString();
    const firstPart = raw.split(' / ')[0].trim().toLowerCase();
    const cleaned = firstPart
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9\u0600-\u06FF-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '');
    return cleaned || fallback;
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    const trimmed = query.trim();

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    if (!trimmed) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    // Debounced server-side search
    searchDebounceRef.current = setTimeout(async () => {
      const reqId = ++searchReqIdRef.current;
      try {
        const results = await searchProducts({ text: trimmed, maxResults: 10 });
        if (reqId !== searchReqIdRef.current) return; // stale response

        // Optional client-side category filter (Firestore can't do two array-contains in one query)
        let filtered = results;
        if (selectedCategory) {
          const catId = selectedCategory.id;
          const catName = (selectedCategory.name || '').toLowerCase();
          filtered = results.filter(p => {
            if (p.categories && Array.isArray(p.categories)) {
              return p.categories.includes(catId) || p.categories.some(c => (c || '').toString().toLowerCase() === catName);
            }
            if (p.category) {
              return (p.category || '').toString().toLowerCase() === catName;
            }
            return false;
          });
        }

        setSearchResults(filtered.slice(0, 5));
        setShowSearchResults(true);
      } catch (err) {
        console.error('Search error:', err);
        setSearchResults([]);
        setShowSearchResults(false);
      }
    }, 250);
  };

  const handleSearch = () => {
    console.log('handleSearch called');
    const params = new URLSearchParams();
    if (searchQuery.trim()) params.append('search', searchQuery.trim());
    if (selectedCategory) params.append('category', selectedCategory.name);
    
    console.log('Navigating to shop with params:', params.toString());
    navigate(`/shop?${params.toString()}`);
    setShowCategoryDropdown(false);
    setShowSearchResults(false);
    setShowMobileSearch(false); // Close mobile search when searching
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <header className="header" data-header>
      
      {/* TOP BAR - Dark Background */}
      <div className="header-top">
        <div className="container">
          
          {/* Mobile Toggle */}
          <button className={`nav-toggle-btn ${navActive ? 'active' : ''}`} aria-label="toggle menu" onClick={toggleNav}>
            <ion-icon name="menu-outline" aria-hidden="true" class="menu-icon"></ion-icon>
            <ion-icon name="close-outline" aria-label="close menu" class="close-icon"></ion-icon>
          </button>

          {/* Language Toggle (Mobile: next to menu) */}
          <button
            className="action-btn lang-btn lang-btn--mobile"
            aria-label="Toggle Language"
            onClick={toggleLanguage}
          >
            <span className="lang-text">{language === 'en' ? 'AR' : 'EN'}</span>
          </button>

          {/* Search Box */}
          <div 
            className={`header-search-wrapper ${showMobileSearch ? 'mobile-search-active' : ''}`}
            ref={searchRef}
          >
            <div className="search-box" style={showSearchResults && searchResults.length > 0 ? {borderRadius: '4px 4px 0 0'} : {}}>
              <input 
                type="text" 
                className="search-input" 
                placeholder={language === 'ar' ? "بحث..." : "Search..."}
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                onFocus={() => { if(searchQuery.trim().length > 0) setShowSearchResults(true); }}
              />
              <div 
                className="search-category" 
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                style={{position: 'relative'}}
              >
                <span>{selectedCategory ? getName(selectedCategory) : (language === 'ar' ? "كل الفئات" : "All categories")}</span>
                <ion-icon name="chevron-down-outline"></ion-icon>
                
                {showCategoryDropdown && (
                  <ul className="category-dropdown-list" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #eee',
                    borderRadius: '4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 1001,
                    maxHeight: '300px',
                    overflowY: 'auto',
                    listStyle: 'none',
                    padding: '5px 0',
                    minWidth: '150px'
                  }}>
                    <li 
                      onClick={(e) => { e.stopPropagation(); setSelectedCategory(null); setShowCategoryDropdown(false); }}
                      style={{padding: '8px 15px', cursor: 'pointer', fontSize: '0.9rem', color: '#333'}}
                      onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      {language === 'ar' ? "كل الفئات" : "All categories"}
                    </li>
                    {categories.filter(c => !c.parentId).map(cat => (
                      <li 
                        key={cat.id} 
                        onClick={(e) => { e.stopPropagation(); setSelectedCategory(cat); setShowCategoryDropdown(false); }}
                        style={{padding: '8px 15px', cursor: 'pointer', fontSize: '0.9rem', color: '#333'}}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      >
                        {getName(cat)}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              <button className="search-btn" onClick={handleSearch}>
                <ion-icon name="search-outline"></ion-icon>
              </button>

            {/* Live Search Results Dropdown */}
            {showSearchResults && searchResults.length > 0 && (
                <div className="search-results-dropdown" style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #eee',
                    borderTop: 'none',
                    borderRadius: '0 0 4px 4px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    zIndex: 1000,
                    maxHeight: '400px',
                    overflowY: 'auto',
                    listStyle: 'none',
                    padding: '0'
                }}>
                    {searchResults.slice(0, 5).map((product, index) => (
                        <div 
                            key={product.id} 
                            className="search-result-item"
                            style={{
                                padding: '12px 15px',
                                cursor: 'pointer',
                                borderBottom: index < searchResults.slice(0, 5).length - 1 ? '1px solid #eee' : 'none',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}
                        onClick={() => handleSearchResultSelect(product)}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                            <img 
                                src={product.imageUrls?.[0] || product.imageUrl || 'https://placehold.co/40x40?text=No+Image'} 
                                alt={product.name}
                                style={{
                                    width: '40px',
                                    height: '40px',
                                    objectFit: 'cover',
                                    borderRadius: '4px'
                                }}
                            />
                            <div style={{flex: 1}}>
                                <div style={{fontWeight: '500', fontSize: '0.9rem', color: '#333', marginBottom: '2px'}}>
                                    {getName(product)}
                                </div>
                                <div style={{fontSize: '0.8rem', color: '#666'}}>
                                    {product.tastes && Array.isArray(product.tastes) && product.tastes.length > 0 && product.tastes[0]?.price
                                        ? `${product.tastes[0].price} EGP` 
                                        : (product.tastePrices && Array.isArray(product.tastePrices) && product.tastePrices.length > 0 && product.tastePrices[0]
                                            ? `${product.tastePrices[0]} EGP` 
                                            : (product.price && product.price > 0 ? `${product.price} EGP` : 'Price on request'))}
                                </div>
                            </div>
                        </div>
                    ))}
                    <div 
                        onMouseDown={() => {
                            handleSearch();
                        }}
                        onClick={() => {
                            handleSearch();
                        }}
                        style={{
                            padding: '10px', 
                            textAlign: 'center', 
                            color: '#0d918b', 
                            fontWeight: 'bold', 
                            cursor: 'pointer',
                            borderTop: '1px solid #eee',
                            fontSize: '0.9rem'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                    >
                        {language === 'ar' ? "عرض كل النتائج" : "View all results"}
                    </div>
                </div>
            )}
            </div>
          </div>

          {/* Logo */}
          <Link to="/" className="logo">
            <img src={logoUrl} alt="VetCare Logo" style={{width: '120px', height: 'auto', objectFit: 'contain', marginTop: '-18px'}} />
          </Link>

          {/* Actions */}
          <div className="header-actions">
            {/* Mobile Search Icon */}
            <button 
              className="mobile-search-icon action-btn" 
              aria-label="Search"
              onClick={() => setShowMobileSearch(!showMobileSearch)}
            >
              <ion-icon name="search-outline"></ion-icon>
            </button>

            {/* Language Toggle */}
            <button 
              className="action-btn lang-btn" 
              aria-label="Toggle Language"
              onClick={toggleLanguage}
            >
              <span className="lang-text">{language === 'en' ? 'AR' : 'EN'}</span>
            </button>

            {/* Wishlist */}
            <Link className="action-btn" to="/wishlist" aria-label="Wishlist">
              <ion-icon name="heart-outline"></ion-icon>
              {typeof getWishlistCount === 'function' && getWishlistCount() > 0 && (
                <span className="btn-badge">{getWishlistCount()}</span>
              )}
            </Link>

            {/* Cart */}
            <button className="action-btn cart-btn" onClick={toggleCart} aria-label="Cart">
              <ion-icon name="cart-outline"></ion-icon>
              <span className="btn-badge">{getCartCount()}</span>
            </button>
          </div>

        </div>
      </div>

      {/* BOTTOM BAR - Navigation (Desktop Only) */}
      <div className="header-bottom desktop-only-flex">
        <div className="container">
          <ul className="desktop-nav-list">
            <li><Link to="/" className="nav-link">Homepage</Link></li>
            <li><Link to="/shop" className="nav-link">Shop All</Link></li>
            
            {/* Dynamic Categories */}
            {(() => {
                const parents = categories.filter(c => !c.parentId);
                const children = categories.filter(c => c.parentId);
                
                // Limit to first 6 parents to avoid overflow
                return parents.slice(0, 7).map(parent => {
                    const myChildren = children.filter(c => c.parentId === parent.id);
                    const hasChildren = myChildren.length > 0;
                    const parentSlug = toSlug(parent.nameEn || parent.name || parent.nameAr || '', '');
                    const parentUrl = parent.customLink || (parentSlug ? `/shop/${parentSlug}` : '/shop');
                    
                    return (
                        <li key={parent.id} className={hasChildren ? "has-dropdown" : ""}>
                            <Link to={parentUrl} className="nav-link">
                                {getName(parent)}
                                {hasChildren && <ion-icon name="chevron-down-outline"></ion-icon>}
                            </Link>
                            
                            {hasChildren && (
                                <ul className="dropdown-list">
                                    {myChildren.map(child => {
                                      const childSlug = toSlug(child.nameEn || child.name || child.nameAr || '', '');
                                      const baseUrl = child.customLink || parentUrl || '/shop';
                                      const joiner = baseUrl.includes('?')
                                        ? ((baseUrl.endsWith('?') || baseUrl.endsWith('&')) ? '' : '&')
                                        : '?';
                                      const childUrl = `${baseUrl}${joiner}sub=${encodeURIComponent(childSlug || child.name)}`;

                                      return (
                                        <li key={child.id}>
                                          <Link to={childUrl}>
                                            {getName(child)}
                                          </Link>
                                        </li>
                                      );
                                    })}
                                </ul>
                            )}
                        </li>
                    );
                });
            })()}
            
            <li><Link to="/contact" className="nav-link">Contact Us</Link></li>
          </ul>
        </div>
      </div>

      {/* Mobile Drawer Menu */}
      <nav className={`navbar ${navActive ? 'active' : ''}`} data-navbar>
          {/* Mobile Menu */}
          <ul className="navbar-list mobile-only-nav">
            <li className="navbar-item">
              <Link to="/" className="navbar-link" onClick={closeNav}>
                Homepage
              </Link>
            </li>
            <li className="navbar-item">
              <Link to="/shop" className="navbar-link" onClick={closeNav}>
                Shop
              </Link>
            </li>
            
            {/* Dynamic Categories */}
            {(() => {
                const parents = categories.filter(c => !c.parentId);
                const children = categories.filter(c => c.parentId);
                
                return parents.slice(0, 7).map(parent => {
                    const myChildren = children.filter(c => c.parentId === parent.id);
                    const hasChildren = myChildren.length > 0;
                  const parentSlug = toSlug(parent.nameEn || parent.name || parent.nameAr || '', '');
                  const parentUrl = parent.customLink || (parentSlug ? `/shop/${parentSlug}` : '/shop');
                  const isExpanded = expandedMobileParentId === parent.id;
                    
                    return (
                        <li key={parent.id} className={`navbar-item ${hasChildren && isExpanded ? 'is-expanded' : ''}`}>
                          {hasChildren ? (
                            <div className="navbar-link navbar-link--expandable">
                              <Link to={parentUrl} className="navbar-link-text" onClick={closeNav}>
                                {getName(parent)}
                              </Link>
                              <button
                                type="button"
                                className="navbar-link-toggle"
                                aria-label={`Toggle ${getName(parent)} subcategories`}
                                aria-expanded={isExpanded}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setExpandedMobileParentId(prev => (prev === parent.id ? null : parent.id));
                                }}
                              >
                                <ion-icon name={isExpanded ? 'chevron-down-outline' : 'chevron-forward-outline'}></ion-icon>
                              </button>
                            </div>
                          ) : (
                            <Link to={parentUrl} className="navbar-link" onClick={closeNav}>
                              {getName(parent)}
                            </Link>
                          )}

                          {hasChildren && (
                            <ul className={`navbar-sublist ${isExpanded ? 'show' : ''}`}>
                              {myChildren.map(child => {
                                const childSlug = toSlug(child.nameEn || child.name || child.nameAr || '', '');
                                const baseUrl = child.customLink || parentUrl || '/shop';
                                const joiner = baseUrl.includes('?')
                                  ? ((baseUrl.endsWith('?') || baseUrl.endsWith('&')) ? '' : '&')
                                  : '?';
                                const childUrl = `${baseUrl}${joiner}sub=${encodeURIComponent(childSlug || child.name)}`;

                                return (
                                  <li key={child.id} className="navbar-subitem">
                                    <Link to={childUrl} className="navbar-sublink" onClick={closeNav}>
                                      {getName(child)}
                                    </Link>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                        </li>
                    );
                });
            })()}
            
            <li className="navbar-item">
              <Link to="/contact" className="navbar-link" onClick={closeNav}>
                Contact Us
              </Link>
            </li>
          </ul>
      </nav>

    </header>
  );
}
